/**
 * In-app link.
 *
 * Renders a real `<a href>` (crawlable, works without JavaScript) and
 * upgrades a plain left-click to a History-API navigation. Optional
 * `event`/`source` fire a Umami usage event programmatically — never via
 * `data-umami-event`, because Umami's own anchor handler would force a
 * full page load after our pushState.
 */

import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { navigate } from '../lib/router';
import { track } from '../lib/analytics';

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  /** Umami event name (kebab-case singular) fired on activation */
  event?: string;
  /** Umami `source` property — the surface this link sits on */
  source?: string;
  children: ReactNode;
}

function isPlainLeftClick(e: MouseEvent<HTMLAnchorElement>): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

export default function Link({ to, event, source, onClick, target, children, ...rest }: LinkProps) {
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (event) track(event, source ? { source } : undefined);
    if (e.defaultPrevented || target === '_blank' || !isPlainLeftClick(e)) return;
    e.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} target={target} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
